"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

function DashboardMockup() {
  return (
    <div className="relative">
      <div className="bg-landing-surface/30 rounded-3xl aspect-[4/3] w-full flex items-center justify-center p-6 overflow-hidden">
        <div className="w-full h-full bg-white rounded-xl shadow-2xl relative overflow-hidden flex">
          {/* Sidebar */}
          <div className="w-16 md:w-20 bg-landing-text flex flex-col items-center py-6 gap-6">
            <div className="w-8 h-8 rounded bg-landing-primary flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-sm">grid_view</span>
            </div>
            <div className="w-8 h-8 rounded flex items-center justify-center">
              <span className="material-symbols-outlined text-white/40 text-sm">description</span>
            </div>
            <div className="w-8 h-8 rounded flex items-center justify-center">
              <span className="material-symbols-outlined text-white/40 text-sm">settings</span>
            </div>
          </div>
          {/* Main Content */}
          <div className="flex-1 flex flex-col bg-gray-50">
            <div className="h-12 bg-white border-b border-gray-200 flex items-center px-4 justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400/20" />
                <div className="w-3 h-3 rounded-full bg-yellow-400/20" />
                <div className="w-3 h-3 rounded-full bg-green-400/20" />
              </div>
              <div className="h-6 w-32 bg-gray-100 rounded-full" />
            </div>
            <div className="flex flex-1 overflow-hidden">
              {/* Doc List */}
              <div className="hidden sm:block w-40 border-r border-gray-200 p-4 space-y-3 bg-white">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Documents</div>
                <div className="space-y-2">
                  <div className="p-2 bg-landing-primary/5 rounded border border-landing-primary/20 flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-landing-primary flex items-center justify-center">
                      <span className="material-symbols-outlined text-[10px] text-white">check</span>
                    </div>
                    <div className="h-2 w-16 bg-landing-primary/20 rounded" />
                  </div>
                  <div className="p-2 bg-gray-50 rounded border border-gray-100 flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-landing-surface/40" />
                    <div className="h-2 w-16 bg-gray-200 rounded" />
                  </div>
                  <div className="p-2 bg-gray-50 rounded border border-gray-100 flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-gray-100" />
                    <div className="h-2 w-16 bg-gray-200 rounded" />
                  </div>
                </div>
              </div>
              {/* Main Panel */}
              <div className="flex-1 p-4 md:p-6">
                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm space-y-4">
                  <div className="flex justify-between">
                    <div className="h-4 w-32 bg-gray-100 rounded" />
                    <div className="h-4 w-12 bg-landing-primary/20 rounded" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="h-2 w-full bg-landing-surface/20 rounded" />
                      <div className="h-8 w-full bg-gray-50 rounded" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-full bg-landing-surface/20 rounded" />
                      <div className="h-8 w-full bg-landing-primary/10 rounded flex items-center px-3">
                        <div className="h-2 w-12 bg-landing-primary rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                  <div className="h-24 w-full bg-gray-50 rounded border border-dashed border-gray-300 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="material-symbols-outlined text-landing-primary opacity-40">finance</span>
                      <div className="h-2 w-24 bg-gray-200 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HeroSection() {
  return (
    <section className="min-h-screen flex items-center bg-landing-bg">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-xl"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-bold tracking-tight text-landing-text leading-[1.05] mb-8">
              Your agentic CPA workforce.
            </h1>
            <p className="text-lg sm:text-xl text-landing-text-secondary leading-relaxed mb-10">
              Upload any client&apos;s tax documents and Dezu automatically
              generates their return. Accurate, fast, and secure.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                href="https://cal.com/riyadev/15min"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-landing-primary text-white px-8 py-3.5 rounded-full font-bold text-base hover:bg-landing-primary-hover transition-colors hover:scale-105 active:scale-95 shadow-lg"
              >
                Book a Demo
              </Link>
              <Link
                href="/login"
                className="flex items-center gap-1.5 px-6 py-3.5 text-base font-semibold text-landing-text-secondary hover:text-landing-text transition-colors"
              >
                Login <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <DashboardMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
